import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { CheckIcon, StarIcon, MenuIcon, XIcon } from 'lucide-react';
import { usePlans } from '../lib/plansClient';
import { FEATURE_BY_ID } from '../lib/featureCatalog';

import { resolvePlanFeatures as sharedResolve } from '../lib/planFeatures';

// resolvePlanFeatures — Landing sayfasında gösterilen madde listesini seçer.
// Öncelik: Ultra Admin > Paket Düzenle > "Ana Sayfa Özellik Maddeleri" (marketing_features)
//          → Backend teknik features feature-ID listesi (FEATURE_BY_ID ile Türkçeye çevrilir)
//          → Hardcoded PACKAGE_FEATURES fallback (Ultra Admin hiç dokunmadıysa)
const resolvePlanFeatures = (plan) =>
  sharedResolve(plan, {
    featureIdToName: (id) => FEATURE_BY_ID[id]?.name,
    fallbackByPlanId: PACKAGE_FEATURES,
  });

// GERÇEK Paket Özellikleri - Kullanıcı tarafından sağlanan tablo
const PACKAGE_FEATURES = {
  starter: [
    'Kişisel Dashboard',
    'Tüm talepleri Görüntüleme',
    'Temel Para Biriminde avans talebi',
    'Standart Avans Talebi',
    'Standart Avans Kapama Süresi',
    'Avans Onay Fonksiyonu',
    'Avans Kapama Fonksiyonu',
    'Standart Kullanıcı Hiyerarşi',
    'Genel Avans Raporları',
    'Kullanıcı Yönetimi',
    'Kategori Yönetimi',
    'Kullanıcı İzinleri',
    'Rol Bazlı İzinler',
    'Genel Ayarlar',
    'Genişletilmiş Filtreler',
    'Mobil Cihazlardan Tam Erişim'
  ],
  professional: [
    'Profesyonel Dashboard',
    'Tüm talepleri Görüntüleme',
    'Çoklu Para Birimi avans talebi',
    'Profesyonel Avans Talebi Fonksiyonları',
    'Profesyonel Avans Kapama Fonksiyonları',
    'Avans Onay Fonksiyonu',
    'Avans Kapama Fonksiyonu',
    'Profesyonel Poziyon Hiyerarşisi',
    'Genel Avans Raporları',
    'Kullanıcı Yönetimi',
    'Kategori Yönetimi',
    'Kullanıcı İzinleri',
    'Rol Bazlı İzinler',
    'Genel Ayarlar',
    'Genişletilmiş Filtreler',
    'Mobil Cihazlardan Tam Erişim',
    'Finans Onay Fonksiyonu',
    'Avans Kapama Belge Modülü',
    'Cari hsap takip Modülü',
    'Email Bildirimleri',
    'Whatsapp Entegrasyonu',
    'Excel Export',
    'PDF Export',
    'Avans Uyarı Sistemi'
  ],
  enterprise: [
    'Profesyonel Dashboard',
    'Tüm talepleri Görüntüleme',
    'Çoklu Para Birimi avans talebi',
    'Profesyonel Avans Talebi Fonksiyonları',
    'Profesyonel Avans Kapama Fonksiyonları',
    'Avans Onay Fonksiyonu',
    'Avans Kapama Fonksiyonu',
    'Ultra Pozisyon Hiyerarşisi',
    'Genel Avans Raporları',
    'Kullanıcı Yönetimi',
    'Kategori Yönetimi',
    'Kullanıcı İzinleri',
    'Rol Bazlı İzinler',
    'Genel Ayarlar',
    'Genişletilmiş Filtreler',
    'Mobil Cihazlardan Tam Erişim',
    'Finans Onay Fonksiyonu',
    'Avans Kapama Belge Modülü',
    'Cari hsap takip Modülü',
    'Şirket içi Mesajlaşma',
    'İşlem Bazlı Avans Raporu',
    'Kullanıcı Bazlı Avans Raporu',
    'Şirket Bazlı Avans Raporu',
    'Yapay Zeka Önerileri',
    'Email Bildirimleri',
    'Whatsapp Entegrasyonu',
    'Excel Export',
    'PDF Export',
    'Avans Uyarı Sistemi'
  ]
};

// Feature ID'den Türkçe açıklama mapping (backend ile uyumluluk için)
const FEATURE_LABELS = {
  'new_advance_request': 'Avans talebi oluşturma',
  'advance_approval': 'Yönetici onay süreci',
  'finance_approval': 'Finans onayı',
  'advance_closing': 'Avans kapatma',
  'expense_reporting': 'Masraf raporlama',
  'pending_approvals': 'Bekleyen onaylar',
  'approved_advances': 'Onaylanan avanslar',
  'closed_advances': 'Kapatılan avanslar',
  'current_account': 'Cari hesap takibi',
  'all_requests': 'Tüm talepler',
  'user_management': 'Kullanıcı yönetimi',
  'role_hierarchy': 'Rol hiyerarşisi',
  'category_management': 'Kategori yönetimi',
  'user_permissions': 'Kullanıcı yetkileri',
  'role_permissions': 'Rol izinleri',
  'advance_rules': 'Avans kuralları',
  'general_settings': 'Genel ayarlar',
  'email_notifications': 'E-posta bildirimleri',
  'whatsapp_integration': 'WhatsApp entegrasyonu',
  'bulk_operations': 'Toplu işlemler',
  'advanced_filtering': 'Gelişmiş filtreleme',
  'excel_export': 'Excel dışa aktarma',
  'pdf_export': 'PDF dışa aktarma',
  'dashboard_analytics': 'Dashboard & analitik',
  'mobile_responsive': 'Mobil uyumlu',
  'api_access': 'API erişimi',
  'custom_branding': 'Özel markalama',
  'priority_support': 'Öncelikli destek',
  'multi_currency': 'Çoklu para birimi',
  'budget_tracking': 'Bütçe takibi'
};

const LandingPage = () => {
  const [activePricing, setActivePricing] = useState('yearly');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Backend'den paketleri al
  const { plans: backendPlans, loading: plansLoading } = usePlans();

  // Fallback: Backend'den veri gelmezse kullanılacak GERÇEK paket değerleri
  const defaultPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price_monthly: 29,
      price_yearly: 29,
      currency: 'eur',
      user_limit: 5,
      features: PACKAGE_FEATURES.starter,
      emoji: '🚀',
      color: 'blue'
    },
    {
      id: 'professional',
      name: 'Professional',
      price_monthly: 39,
      price_yearly: 39,
      currency: 'eur',
      user_limit: 15,
      features: PACKAGE_FEATURES.professional,
      emoji: '👑',
      color: 'purple',
      is_featured: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price_monthly: 49,
      price_yearly: 49,
      currency: 'eur',
      user_limit: 50,
      features: PACKAGE_FEATURES.enterprise,
      emoji: '🏢',
      color: 'indigo'
    }
  ];

  // Backend'den gelen verileri normalize et (field name uyumsuzluğu için)
  const normalizePlans = (plans) => {
    return plans.map(plan => ({
      id: plan.id || plan.code,
      name: plan.name || plan.title,
      price_monthly: plan.price_monthly || plan.price,
      price_yearly: plan.price_yearly || plan.price,
      currency: plan.currency?.toLowerCase() || 'usd',
      user_limit: plan.user_limit || plan.userLimit || 0,
      features: plan.features || [],
      marketing_features: plan.marketing_features || plan.marketingFeatures || [],
      emoji: plan.emoji,
      color: plan.color,
      is_featured: plan.is_featured || plan.isFeatured || false,
      description: plan.description
    }));
  };
  
  // Backend'den gelen verileri kullan, normalize et, yoksa default'a düş
  const plans = backendPlans.length > 0 ? normalizePlans(backendPlans) : defaultPlans;
  
  // Para birimi sembolü
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'try': '₺',
      'usd': '$',
      'eur': '€',
      'gbp': '£'
    };
    return symbols[currency?.toLowerCase()] || '$';
  };

  // Paket rengine göre stil ayarları
  const getColorStyles = (planId) => {
    const colorMap = {
      'starter': {
        border: 'border-blue-300',
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        btnBg: 'bg-blue-600 hover:bg-blue-700',
        gradientFrom: 'from-blue-50',
        gradientTo: 'to-blue-100',
        borderColor: 'border-blue-200'
      },
      'professional': {
        border: 'border-purple-300',
        bg: 'bg-purple-100',
        text: 'text-purple-600',
        btnBg: 'bg-purple-600 hover:bg-purple-700',
        gradientFrom: 'from-purple-50',
        gradientTo: 'to-purple-100',
        borderColor: 'border-purple-200'
      },
      'enterprise': {
        border: 'border-indigo-300',
        bg: 'bg-indigo-100',
        text: 'text-indigo-600',
        btnBg: 'bg-indigo-600 hover:bg-indigo-700',
        gradientFrom: 'from-indigo-50',
        gradientTo: 'to-indigo-100',
        borderColor: 'border-indigo-200'
      }
    };
    return colorMap[planId] || colorMap['starter'];
  };

  const features = [
    {
      title: 'Otomatik Avans Yönetimi',
      description: 'Personel avans taleplerini otomatik olarak takip edin ve onaylayın.',
      image: 'https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b'
    },
    {
      title: 'Çoklu Rol Desteği',
      description: 'Farklı departmanlar için özel yetkilendirmeler yapın.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f'
    },
    {
      title: 'Detaylı Raporlama',
      description: 'Avans durumlarını anlık olarak takip edin ve raporlayın.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71'
    }
  ];

  const [testimonials, setTestimonials] = useState([
    {
      name: 'Ahmet Kaya',
      company: 'TechnoSoft A.Ş.',
      comment: 'Advancecash.io sayesinde avans süreçlerimiz tamamen dijitalleşti. Artık kağıt işi yok!',
      rating: 5
    },
    {
      name: 'Elif Demir',
      company: 'İnnovatif Çözümler Ltd.',
      comment: 'Kullanımı çok kolay ve ekip yönetimi mükemmel. Herkese tavsiye ederim.',
      rating: 5
    },
    {
      name: 'Mehmet Öz',
      company: 'Digital Pro',
      comment: 'Muhasebe takibi artık çok daha kolay. Zaman ve emek tasarrufu sağladık.',
      rating: 5
    }
  ]);

  // Fetch dynamic testimonials from Ultra Admin. Falls back to the hardcoded
  // seed above when the backend returns an empty list (fresh install).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/testimonials`);
        const list = res.data?.testimonials || [];
        if (!cancelled && list.length > 0) setTestimonials(list);
      } catch (e) {
        // silent — landing page must never break because of a testimonial fetch
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md fixed w-full z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold gradient-text" data-testid="logo">
                Advancecash.io
              </h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="nav-link">Özellikler</a>
              <a href="#pricing" className="nav-link">Fiyatlar</a>
              <a href="#testimonials" className="nav-link">Referanslar</a>
              <Link to="/login" className="nav-link">Giriş Yap</Link>
              <Link to="/ultra-admin" className="text-xs text-gray-500 hover:text-gray-700 transition-colors">
                Ultra Admin
              </Link>
              <Link to="/register">
                <Button className="btn-primary" data-testid="get-started-btn">
                  <span>Başlayın</span>
                </Button>
              </Link>
            </div>
            
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                {mobileMenuOpen ? (
                  <XIcon className="w-6 h-6" />
                ) : (
                  <MenuIcon className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col space-y-3">
                <a href="#features" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Özellikler</a>
                <a href="#pricing" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Fiyatlar</a>
                <a href="#testimonials" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Referanslar</a>
                <Link to="/login" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Giriş Yap</Link>
                <Link to="/ultra-admin" className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setMobileMenuOpen(false)}>
                  Ultra Admin
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="btn-primary w-full" data-testid="get-started-btn-mobile">
                    <span>Başlayın</span>
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section - Full Screen */}
      <section className="relative min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-black bg-opacity-40">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.1) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="mb-6">
                <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/30">
                  🚀 Türkiye'nin #1 İş Avans Yazılımı
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                İş Avanslarını 
                <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Akıllıca Yönetin
                </span>
              </h1>
              
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                <strong className="text-white">Advancecash.io</strong> ile şirket avans süreçlerinizi tamamen dijitalleştirin. 
                AI destekli raporlar, anlık uyarılar ve mobil erişim ile maliyetlerinizi %40'a kadar düşürün.
              </p>

              {/* Key Benefits */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center text-white">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                    <CheckIcon className="w-5 h-5" />
                  </div>
                  <span className="font-medium">Anlık Onay Sistemi</span>
                </div>
                <div className="flex items-center text-white">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                    <CheckIcon className="w-5 h-5" />
                  </div>
                  <span className="font-medium">Mobil Erişim</span>
                </div>
                <div className="flex items-center text-white">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mr-3">
                    <CheckIcon className="w-5 h-5" />
                  </div>
                  <span className="font-medium">AI Destekli Raporlar</span>
                </div>
                <div className="flex items-center text-white">
                  <div className="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center mr-3">
                    <CheckIcon className="w-5 h-5" />
                  </div>
                  <span className="font-medium">Otomatik Uyarılar</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/register?plan=free_trial">
                  <Button className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-8 py-4 text-lg font-semibold shadow-2xl transform hover:scale-105 transition-all duration-200" data-testid="get-started-hero">
                    <span>🎯 14 Gün Ücretsiz Deneyin</span>
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center text-sm text-gray-300">
                <CheckIcon className="w-4 h-4 text-green-400 mr-2" />
                <span>Kredi kartı gerekmez • Anında kurulum • 5 dakikada başlayın</span>
              </div>
            </div>
            
            <div className="relative">
              {/* Interactive Dashboard Preview */}
              <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20">
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Avans Dashboard</h3>
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                      <div className="text-2xl font-bold text-blue-600">47</div>
                      <div className="text-xs text-blue-700">Aktif Avans</div>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                      <div className="text-2xl font-bold text-green-600">₺127K</div>
                      <div className="text-xs text-green-700">Bu Ay</div>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                      <div className="text-2xl font-bold text-purple-600">2.3dk</div>
                      <div className="text-xs text-purple-700">Ort. Onay</div>
                    </div>
                  </div>
                  
                  {/* Recent Transaction */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                          AY
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Ahmet Yılmaz</div>
                          <div className="text-xs text-gray-500">Satış Toplantısı</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-900">₺2,500</div>
                        <Badge className="bg-green-100 text-green-800 text-xs">Onaylandı</Badge>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                          MD
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Mehmet Demir</div>
                          <div className="text-xs text-gray-500">Yüksek Harcama Uyarısı!</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-orange-600">₺8,750</div>
                        <Badge className="bg-yellow-100 text-yellow-800 text-xs">Limit Aşımı</Badge>
                      </div>
                    </div>
                  </div>
                  
                  {/* AI Insight */}
                  <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-lg">🤖</span>
                      <span className="font-semibold text-indigo-900">AI Önerisi</span>
                    </div>
                    <p className="text-sm text-indigo-700">
                      Bu ay belgesiz fatura oranı %15 arttı. Otomatik uyarı sistemini aktifleştirmenizi öneriyoruz.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center text-white text-2xl animate-bounce">
                📱
              </div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-green-400 to-teal-500 rounded-full flex items-center justify-center text-white text-xl animate-pulse">
                ⚡
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Impact Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Şirketinize Nasıl Değer Katıyor?
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Advancecash.io ile avans süreçlerinizdeki verimsizlikleri ortadan kaldırın, 
              maliyetlerinizi düşürün ve kontrolü tamamen elinize alın.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* Cost Reduction */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-red-100 hover:shadow-2xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6">
                <span className="text-white text-2xl">💰</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Maliyet Düşürme</h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                  <span>Kağıt evrak maliyetini %100 ortadan kaldırın</span>
                </div>
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                  <span>Manuel süreç maliyetlerini %60 azaltın</span>
                </div>
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                  <span>Gereksiz avans taleplerini %40 düşürün</span>
                </div>
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                  <span>İnsan kaynakları verimliliği %50 artırın</span>
                </div>
              </div>
            </div>

            {/* Risk Management */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-orange-100 hover:shadow-2xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center mb-6">
                <span className="text-white text-2xl">🛡️</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Risk Yönetimi</h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-orange-500 mr-2 flex-shrink-0" />
                  <span>Yüksek harcamalarda anlık uyarı alın</span>
                </div>
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-orange-500 mr-2 flex-shrink-0" />
                  <span>Kullanıcı bazlı harcama limitleri</span>
                </div>
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-orange-500 mr-2 flex-shrink-0" />
                  <span>Belgesiz fatura oranı takibi</span>
                </div>
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-orange-500 mr-2 flex-shrink-0" />
                  <span>Açık avans limitlerini kontrol edin</span>
                </div>
              </div>
            </div>

            {/* Efficiency */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-green-100 hover:shadow-2xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6">
                <span className="text-white text-2xl">⚡</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Verimlilik</h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Avans onay süresi: 8 saatten 2 dakikaya</span>
                </div>
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Mobil uygulama ile her yerden erişim</span>
                </div>
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Otomatik raporlama ve analiz</span>
                </div>
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Dünyanın her yerinden anlık takip</span>
                </div>
              </div>
            </div>
          </div>

          {/* ROI Calculator */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-8 lg:p-12 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">Yatırımınızın Geri Dönüşü</h3>
            <p className="text-xl mb-8 opacity-90">
              Ortalama şirketlerimiz <strong>3 ay içinde</strong> yatırımlarını geri kazanıyor ve 
              <strong> yıllık %300 tasarruf</strong> sağlıyor.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <div className="text-3xl font-bold mb-2">3 Ay</div>
                <div className="text-sm opacity-80">ROI Süresi</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <div className="text-3xl font-bold mb-2">%300</div>
                <div className="text-sm opacity-80">Yıllık Tasarruf</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <div className="text-3xl font-bold mb-2">₺50K+</div>
                <div className="text-sm opacity-80">Ortalama Kazanım</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Features by Package - DİNAMİK */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Gelişmiş Özellikler ve AI Destekli Çözümler
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Paket seviyenize göre özelleştirilmiş çözümlerle şirketinizin ihtiyaçlarını tam olarak karşılayın.
            </p>
          </div>

          {/* Feature Categories - DİNAMİK */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {plans.filter(p => p.id !== 'free_trial').map((plan) => {
              const styles = getColorStyles(plan.id);
              const isProfessional = plan.id === 'professional' || plan.is_featured;
              
              // Backend'den (Ultra Admin > Paket Yönetimi) gelen feature
              // ID'lerini Türkçe adlarına çevirir; boşsa marketing metnine
              // fallback yapar.
              const planFeatures = resolvePlanFeatures(plan);

              return (
                <div 
                  key={`feature-${plan.id}`}
                  className={`bg-gradient-to-br ${styles.gradientFrom} ${styles.gradientTo} rounded-2xl p-8 border ${isProfessional ? 'border-2' : ''} ${styles.borderColor} ${isProfessional ? 'relative' : ''}`}
                >
                  {isProfessional && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className={`${styles.btnBg} text-white px-3 py-1 rounded-full text-xs font-medium`}>
                        En Popüler
                      </span>
                    </div>
                  )}
                  <div className="flex items-center mb-6">
                    <div className={`w-12 h-12 ${styles.btnBg.split(' ')[0]} rounded-xl flex items-center justify-center mr-4`}>
                      <span className="text-white text-xl">
                        {plan.emoji || (plan.id === 'starter' ? '🚀' : plan.id === 'professional' ? '👑' : '🏢')}
                      </span>
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${plan.id === 'starter' ? 'text-blue-900' : plan.id === 'professional' ? 'text-purple-900' : 'text-indigo-900'}`}>
                        {plan.name} Özellikleri
                      </h3>
                      <p className={`text-sm ${plan.id === 'starter' ? 'text-blue-700' : plan.id === 'professional' ? 'text-purple-700' : 'text-indigo-700'}`}>
                        {plan.description || (plan.id === 'starter' ? 'Temel avans yönetimi' : plan.id === 'professional' ? 'AI destekli akıllı yönetim' : 'Kurumsal seviye çözümler')}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {planFeatures.length > 0 ? (
                      planFeatures.slice(0, 6).map((feature, index) => (
                        <div key={index} className={`flex items-center ${plan.id === 'starter' ? 'text-blue-800' : plan.id === 'professional' ? 'text-purple-800' : 'text-indigo-800'}`}>
                          <CheckIcon className={`w-4 h-4 ${plan.id === 'starter' ? 'text-blue-600' : plan.id === 'professional' ? 'text-purple-600' : 'text-indigo-600'} mr-2 flex-shrink-0`} />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))
                    ) : (
                      <div className={`flex items-center ${plan.id === 'starter' ? 'text-blue-800' : plan.id === 'professional' ? 'text-purple-800' : 'text-indigo-800'}`}>
                        <CheckIcon className={`w-4 h-4 ${plan.id === 'starter' ? 'text-blue-600' : plan.id === 'professional' ? 'text-purple-600' : 'text-indigo-600'} mr-2 flex-shrink-0`} />
                        <span className="text-sm">Tüm temel özellikler dahil</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Smart Monitoring */}
          <div className="mt-16 bg-gradient-to-r from-gray-900 to-gray-700 rounded-3xl p-8 lg:p-12 text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-6">Akıllı İzleme ve Uyarı Sistemi</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <span className="text-white text-sm">🚨</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Yüksek Harcama Uyarıları</h4>
                      <p className="text-gray-300 text-sm">Belirlenen limitler aşıldığında anında bildirim alın</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <span className="text-white text-sm">📄</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Belgesiz Fatura Takibi</h4>
                      <p className="text-gray-300 text-sm">Belgesiz harcama oranı artışında otomatik uyarı</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <span className="text-white text-sm">📊</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Avans Kapama Takibi</h4>
                      <p className="text-gray-300 text-sm">Açık avanslar için otomatik takip ve bildirim sistemi</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="space-y-4">
                  <div className="bg-red-500/20 border border-red-400 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Uyarı: Limit Aşımı</span>
                      <span className="text-xs text-red-300">Az önce</span>
                    </div>
                    <p className="text-sm text-red-200 mt-1">
                      Mehmet Demir aylık limitini %120 aştı (₺8,750)
                    </p>
                  </div>
                  <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Belge Uyarısı</span>
                      <span className="text-xs text-yellow-300">5dk önce</span>
                    </div>
                    <p className="text-sm text-yellow-200 mt-1">
                      Bu ay belgesiz fatura oranı %15 arttı
                    </p>
                  </div>
                  <div className="bg-blue-500/20 border border-blue-400 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Avans Takibi</span>
                      <span className="text-xs text-blue-300">1s önce</span>
                    </div>
                    <p className="text-sm text-blue-200 mt-1">
                      15 gün açık avans: Ayşe K. (₺3,200)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Basit ve Şeffaf Fiyatlandırma
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              İhtiyacınıza uygun planı seçin
            </p>
            
            {/* Pricing Toggle */}
            <div className="flex items-center justify-center mb-8">
              <span className={`mr-3 ${activePricing === 'monthly' ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
                Aylık
              </span>
              <button 
                onClick={() => setActivePricing(activePricing === 'monthly' ? 'yearly' : 'monthly')}
                className="relative inline-flex h-6 w-11 items-center rounded-full bg-teal-600 transition-colors"
                data-testid="pricing-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${activePricing === 'yearly' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className={`ml-3 ${activePricing === 'yearly' ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
                Yıllık
              </span>
              <span className="ml-2 bg-teal-100 text-teal-800 px-2 py-1 rounded-full text-sm font-medium">
                %20 İndirim
              </span>
            </div>
          </div>

          {/* Pricing Cards - DİNAMİK */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.filter(p => p.id !== 'free_trial').map((plan) => {
              const styles = getColorStyles(plan.id);
              const currencySymbol = getCurrencySymbol(plan.currency);
              const monthlyPrice = plan.price_monthly || 0;
              const yearlyPrice = activePricing === 'yearly' 
                ? Math.round(monthlyPrice * 12 * 0.8) // %20 indirim
                : monthlyPrice;
              const displayPrice = activePricing === 'yearly' ? yearlyPrice : monthlyPrice;
              
              // Backend'den (Ultra Admin > Paket Yönetimi) gelen feature
              // ID'lerini Türkçe adlarına çevirir; boşsa marketing metnine
              // fallback yapar. Pricing kartında sadece ilk 5 gösterilir.
              const planFeatures = resolvePlanFeatures(plan).slice(0, 5);

              const isProfessional = plan.id === 'professional' || plan.is_featured;

              return (
                <Card 
                  key={plan.id}
                  className={`card-hover p-6 border ${isProfessional ? 'border-2 shadow-xl relative transform scale-105' : 'border-gray-200'} hover:${styles.border} transition-all duration-300`}
                >
                  {isProfessional && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className={`${styles.btnBg} text-white px-4 py-1 rounded-full text-sm font-medium`}>
                        En Popüler
                      </span>
                    </div>
                  )}
                  <div className="text-center">
                    <div className={`w-12 h-12 ${styles.bg} rounded-lg flex items-center justify-center mx-auto mb-4`}>
                      <span className={`${styles.text} text-xl`}>
                        {plan.emoji || (plan.id === 'starter' ? '🚀' : plan.id === 'professional' ? '👑' : '🏢')}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {plan.name}
                    </h3>
                    <div className="mb-4">
                      <span className={`${isProfessional ? 'text-4xl gradient-text' : 'text-3xl text-gray-900'} font-bold`}>
                        {currencySymbol}{displayPrice}
                      </span>
                      <span className="text-gray-600 ml-1">/ay</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-6">
                      {plan.user_limit} kullanıcıya kadar
                    </p>

                    <ul className="text-left mb-6 space-y-3">
                      {planFeatures.length > 0 ? (
                        planFeatures.map((feature, index) => (
                          <li key={index} className="flex items-center">
                            <CheckIcon className={`w-4 h-4 ${styles.text} mr-2 flex-shrink-0`} />
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </li>
                        ))
                      ) : (
                        // Fallback: Özellik yoksa genel açıklamalar
                        <li className="flex items-center">
                          <CheckIcon className={`w-4 h-4 ${styles.text} mr-2 flex-shrink-0`} />
                          <span className="text-gray-700 text-sm">Tüm temel özellikler</span>
                        </li>
                      )}
                    </ul>

                    <Link to="/pricing">
                      <Button className={`w-full ${styles.btnBg} text-white`} data-testid={`${plan.id}-details-btn`}>
                        Tümünü Gör
                      </Button>
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>
          
          {/* Free Trial Notice */}
          <div className="text-center mt-8">
            <p className="text-gray-600 text-lg">
              <span className="font-semibold">14 gün ücretsiz deneme</span> • İstediğiniz zaman iptal edin
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Müşterilerimiz Ne Diyor?
            </h2>
            <p className="text-xl text-gray-600">
              Binlerce şirket Advancecash.io ile avans süreçlerini yönetiyor
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="card-hover p-6 text-center">
                <div className="mb-4">
                  <img 
                    src={testimonial.avatar_url || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"}
                    alt={testimonial.company || 'Şirket'}
                    className="w-16 h-16 rounded-full mx-auto mb-4 object-cover"
                  />
                  <div className="flex justify-center mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>
                <blockquote className="text-gray-700 mb-4 italic">
                  "{testimonial.comment}"
                </blockquote>
                <cite className="text-sm text-gray-600">
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div>{testimonial.company}</div>
                </cite>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-bg">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Avans Süreçlerinizi Bugün Dijitalleştirin
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Advancecash.io ile başlamak sadece birkaç dakika sürüyor
          </p>
          <Link to="/register">
            <Button className="bg-white text-teal-600 hover:bg-gray-100 text-lg px-8 py-4 font-semibold" data-testid="final-cta-btn">
              Ücretsiz Hesap Oluşturun
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <h3 className="text-2xl font-bold mb-4">Advancecash.io</h3>
              <p className="text-gray-400 mb-4">
                İş avans yönetiminde yenilikçi çözümler sunuyoruz.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Ürün</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white">Özellikler</a></li>
                <li><a href="#pricing" className="hover:text-white">Fiyatlar</a></li>
                <li><a href="#" className="hover:text-white">Entegrasyonlar</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Destek</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Yardım Merkezi</a></li>
                <li><a href="#" className="hover:text-white">İletişim</a></li>
                <li><a href="#" className="hover:text-white">API Dokümantasyon</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Advancecash.io. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;